import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useKiosk } from '../../contexts/KioskContext';
import paths from '../../routes/paths';
import { Config } from '../../utils/Config';
import welbeelogo from '../assets/images/welbee_logo.svg';
import '../../views/Kiosk/KioskStyles.scss';

const TRIPLE_TAP_TIMEOUT = 1200; // 1.2 seconds between taps

export default function KioskHeader() {
  const { school, changeMode } = useKiosk();
  const { schoolSlug } = useParams();
  const navigate = useNavigate();
  const [tapCount, setTapCount] = useState(0);
  const tapTimeoutRef = useRef(null);

  const handleLogoClick = useCallback(() => {
    // Clear existing timeout
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
    }

    const newTapCount = tapCount + 1;
    setTapCount(newTapCount);

    if (newTapCount === 3) {
      // Triple tap detected - reset the active mode and return to the mode picker
      // (the bare /kiosk/:schoolSlug URL, which renders the picker once no mode is set).
      setTapCount(0);
      changeMode();
      navigate(paths.kioskSchool.replace(':schoolSlug', schoolSlug));
    } else {
      // Set timeout to reset tap count
      tapTimeoutRef.current = setTimeout(() => {
        setTapCount(0);
      }, TRIPLE_TAP_TIMEOUT);
    }
  }, [tapCount, navigate, schoolSlug, changeMode]);

  // Construct logo URL - check multiple possible locations for logo
  // Priority: 1. school.theme.logoUrl, 2. school.logoUrl, 3. school.theme.headerImageUrl
  // Always use Artifact download endpoint as first priority (same pattern as SurveyView)
  const logoUrl = useMemo(() => {
    if (!school) return null;
    
    // Try different possible logo field locations
    const logoPath = school?.theme?.logoUrl || 
                     school?.logoUrl || 
                     school?.theme?.headerImageUrl ||
                     school?.headerImageUrl;
    
    // Debug: log school data to see what's available
    if (process.env.NODE_ENV === 'development' && !logoPath) {
      console.log('KioskHeader: School data available:', {
        hasTheme: !!school.theme,
        themeKeys: school.theme ? Object.keys(school.theme) : [],
        schoolKeys: Object.keys(school),
        fullSchool: school
      });
    }
    
    if (!logoPath) return null;
    
    // Use Artifact download endpoint as first priority (same pattern as SurveyView)
    // This handles both filenames and full URLs by passing them through the download endpoint
    return `${Config.BASE_URL}/Artifact/downloadlogo?fileName=${logoPath}`;
  }, [school]);

  return (
    <div className="kiosk-header">
      <Box
        component="img"
        src={logoUrl || welbeelogo}
        alt={school?.schoolName || 'Welbee Logo'}
        className="kiosk-logo"
        onClick={handleLogoClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleLogoClick();
          }
        }}
        tabIndex={0}
        role="button"
        aria-label="School logo - triple tap to access admin settings"
      />
      <Box sx={{ flex: 1 }} />
    </div>
  );
}
